"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BillingSummarySchema = exports.SubscriptionStatusSchema = exports.UsageRecordInputSchema = exports.CreditCalculationSchema = exports.ModelPricingSchema = exports.TIER_CREDITS = void 0;
const zod_1 = require("zod");
/*
 * Tier configuration with credit allocations.
 *
 * `free` is a ONE-TIME grant at signup, never refreshed (see CreditResetJob --
 * monthly resets are paid-plans-only). Since the website, Dunea Desktop and
 * Dunea Work share one balance per account, it is the ONE free grant for all
 * three products together: 10 credits (it was 20 on the website plus 10 on
 * each desktop product). The desktop plans' Free tier lists the same 10.
 *
 * The paid website tiers below are no longer sold (Dunea Desktop's Plus/Pro
 * are the plans now); they stay for the grandfathered subscribers on them.
 */
exports.TIER_CREDITS = {
    free: 10,
    // `starter` is the $5/mo entry launch plan — 20 credits a month, renewing like
    // every other paid plan (it was a one-time charge until 2026-08-29 and carried a
    // $1 first month until 2026-09-03). See modules/payments/.../starter-offer.ts.
    starter: 20,
    growth: 100,
    pro: 300,
    /*
     * RETIRED 2026-09-04: `scale` is no longer sold. It stays in this map, in
     * `tierEnum` and in `TIER_RANK` because subscribers who bought it are still on
     * it — dropping the key would resolve their allocation to `undefined` and
     * silently zero their monthly credits. Nothing user-facing offers it: it is out
     * of PAID_TIERS, the pricing page and the in-app plan picker.
     */
    scale: 1500,
};
// Model pricing schema
exports.ModelPricingSchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    provider: zod_1.z.string(),
    model: zod_1.z.string(),
    modelTier: zod_1.z.enum(['lightweight', 'standard', 'premium']),
    creditsPerMInput: zod_1.z.number().nonnegative(),
    creditsPerMOutput: zod_1.z.number().nonnegative(),
    createdAt: zod_1.z.string().datetime().optional(),
});
// Credit calculation result
exports.CreditCalculationSchema = zod_1.z.object({
    provider: zod_1.z.string(),
    model: zod_1.z.string(),
    modelTier: zod_1.z.enum(['lightweight', 'standard', 'premium']),
    promptTokens: zod_1.z.number().int().nonnegative(),
    completionTokens: zod_1.z.number().int().nonnegative(),
    creditsConsumed: zod_1.z.number().nonnegative(),
    breakdown: zod_1.z.object({
        inputCredits: zod_1.z.number().nonnegative(),
        outputCredits: zod_1.z.number().nonnegative(),
        /** Credits for tokens used to create prompt cache (e.g., 1.25x input rate for Anthropic) */
        cacheCreationCredits: zod_1.z.number().nonnegative().optional(),
        /** Credits for tokens read from cache (e.g., 0.1x input rate for Anthropic - 90% savings) */
        cacheReadCredits: zod_1.z.number().nonnegative().optional(),
        /** Credits for extended thinking/reasoning tokens */
        reasoningCredits: zod_1.z.number().nonnegative().optional(),
    }),
});
// Usage record with idempotency
exports.UsageRecordInputSchema = zod_1.z.object({
    userId: zod_1.z.string().uuid(),
    chatId: zod_1.z.string().optional(),
    provider: zod_1.z.string(),
    model: zod_1.z.string(),
    promptTokens: zod_1.z.number().int().nonnegative(),
    completionTokens: zod_1.z.number().int().nonnegative(),
    idempotencyKey: zod_1.z.string(),
    /** Tokens used to create prompt cache (Anthropic: billed at 1.25x input rate) */
    cacheCreationTokens: zod_1.z.number().int().nonnegative().optional(),
    /** Tokens read from prompt cache (Anthropic: billed at 0.1x input rate - 90% savings) */
    cacheReadTokens: zod_1.z.number().int().nonnegative().optional(),
    /** Extended thinking tokens for reasoning models */
    reasoningTokens: zod_1.z.number().int().nonnegative().optional(),
    /** Number of retry attempts for this request */
    retryCount: zod_1.z.number().int().nonnegative().optional(),
});
// Subscription status
exports.SubscriptionStatusSchema = zod_1.z.enum(['none', 'active', 'cancelled', 'past_due']);
// Billing summary for API responses
exports.BillingSummarySchema = zod_1.z.object({
    tier: zod_1.z.enum(['free', 'starter', 'growth', 'pro', 'scale']),
    creditsAllocated: zod_1.z.number().int(),
    creditsRemaining: zod_1.z.number().int(),
    creditsUsed: zod_1.z.number(),
    resetAt: zod_1.z.string().datetime(),
    subscriptionStatus: exports.SubscriptionStatusSchema.optional(),
});
